import { Injectable, inject } from '@angular/core';
import { SupabaseClient } from '@supabase/supabase-js';
import { Post } from '../models/post.model';
import { Topic } from '../models/topic.model';
import { MusicTrack } from '../models/music-track.model';
import { BrandTemplate } from '../models/brand-template.model';
import { ContentDraft, PipelineRun, ContentSource, DraftStatus } from '../models/content-draft.model';
import { ImageLibraryImage, ImageCategory } from '../models/image-library.model';
import { AuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class SupabaseService {
  private get db(): SupabaseClient { return this.auth.client; }
  private readonly auth = inject(AuthService);

  // ── Posts ────────────────────────────────────────────────────────────────
  async loadPosts(): Promise<Post[]> {
    const { data, error } = await this.db
      .from('sw_posts')
      .select('data')
      .order('created_at', { ascending: false });

    if (error) { console.error('[Supabase] loadPosts:', error.message); return []; }
    return (data ?? []).map(r => r.data as Post);
  }

  async upsertPost(post: Post): Promise<void> {
    const { error } = await this.db.from('sw_posts').upsert(<any>{
      id:              post.id,
      post_key:        post.post_key,
      platform:        post.platform,
      format:          post.format,
      style:           post.style,
      template:        post.template ?? 'headline',
      language:        (post as any).language ?? 'de',
      status:          post.status,
      content_pillar:  post.content_pillar,
      topic_key:       post.topic_key ?? null,
      week_tag:        post.week_tag ?? null,
      scheduled_at:    post.scheduled_at ?? null,
      published_at:    post.status === 'published' ? new Date().toISOString() : null,
      r2_image_url:    post.imageUrl ?? null,
      human_edited:    post.human_edited,
      data:            post,
    });
    if (error) {
      console.error('[Supabase] upsertPost:', error.message, error);
      throw new Error(`Supabase upsertPost failed: ${error.message}`);
    }
  }

  async updatePostStatus(id: string, status: string): Promise<void> {
    const { error } = await this.db.from('sw_posts')
      .update({ status, published_at: status === 'published' ? new Date().toISOString() : null })
      .eq('id', id);
    if (error) console.error('[Supabase] updatePostStatus:', error.message);
  }

  async deletePost(id: string): Promise<void> {
    const { error } = await this.db.from('sw_posts').delete().eq('id', id);
    if (error) console.error('[Supabase] deletePost:', error.message);
  }

  async saveInstagramMediaId(id: string, mediaId: string): Promise<void> {
    const { error } = await this.db.from('sw_posts')
      .update({ instagram_media_id: mediaId })
      .eq('id', id);
    if (error) console.error('[Supabase] saveInstagramMediaId:', error.message);
  }

  // ── Topics ───────────────────────────────────────────────────────────────
  async loadTopics(): Promise<Topic[]> {
    const { data, error } = await this.db
      .from('sw_topics')
      .select('key, name, color, description, hashtags, active')
      .eq('active', true)
      .order('sort_order', { ascending: true });

    if (error) { console.error('[Supabase] loadTopics:', error.message); return []; }
    return (data ?? []) as Topic[];
  }

  async upsertTopic(topic: Topic): Promise<void> {
    const { error } = await this.db.from('sw_topics').upsert({
      key:         topic.key,
      name:        topic.name,
      color:       topic.color,
      description: topic.description,
      hashtags:    topic.hashtags ?? [],
      active:      topic.active,
    });
    if (error) console.error('[Supabase] upsertTopic:', error.message);
  }

  // ── Slides ───────────────────────────────────────────────────────────────
  async upsertSlides(postId: string, slides: { index: number; headline: string; body: string; r2_url?: string }[]): Promise<void> {
    if (!slides.length) return;
    const rows = slides.map(s => ({
      post_id:     postId,
      slide_index: s.index,
      headline:    s.headline,
      body:        s.body,
      r2_url:      s.r2_url ?? null,
    }));
    const { error } = await this.db.from('sw_slides').upsert(rows, { onConflict: 'post_id,slide_index' });
    if (error) console.error('[Supabase] upsertSlides:', error.message);
  }

  // ── Publications ─────────────────────────────────────────────────────────
  async savePublication(postId: string, platform: string, result: {
    success: boolean; externalId?: string; externalUrl?: string; errorMessage?: string;
  }): Promise<void> {
    const { error } = await this.db.from('sw_publications').insert({
      post_id:       postId,
      platform,
      status:        result.success ? 'published' : 'failed',
      external_id:   result.externalId ?? null,
      external_url:  result.externalUrl ?? null,
      error_message: result.errorMessage ?? null,
      published_at:  result.success ? new Date().toISOString() : null,
    });
    if (error) console.error('[Supabase] savePublication:', error.message);
  }

  // ── Images ───────────────────────────────────────────────────────────────
  async saveImage(data: {
    postId?: string; source: string; originalUrl?: string;
    r2Key?: string; r2Url?: string; width?: number; height?: number;
    format?: string; dallEPrompt?: string;
  }): Promise<void> {
    const { error } = await this.db.from('sw_images').insert({
      post_id:      data.postId ?? null,
      source:       data.source,
      original_url: data.originalUrl ?? null,
      r2_key:       data.r2Key ?? null,
      r2_url:       data.r2Url ?? null,
      width:        data.width ?? null,
      height:       data.height ?? null,
      format:       data.format ?? null,
      dall_e_prompt: data.dallEPrompt ?? null,
    });
    // ignore FK violations (post not in DB yet) and unique_violation (already saved)
    if (error && error.code !== '23505' && !error.message.includes('foreign key')) {
      console.error('[Supabase] saveImage:', error.message);
    }
  }

  // ── AI Generation Log ────────────────────────────────────────────────────
  async logGeneration(data: {
    postId?: string; type: string; model: string; language?: string;
    template?: string; feedbackUsed?: string; promptTokens?: number;
    completionTokens?: number; durationMs?: number; success?: boolean; errorMessage?: string;
  }): Promise<void> {
    const { error } = await this.db.from('sw_ai_generations').insert({
      post_id:           data.postId ?? null,
      type:              data.type,
      model:             data.model,
      language:          data.language ?? 'de',
      template:          data.template ?? null,
      feedback_used:     data.feedbackUsed ?? null,
      prompt_tokens:     data.promptTokens ?? null,
      completion_tokens: data.completionTokens ?? null,
      duration_ms:       data.durationMs ?? null,
      success:           data.success ?? true,
      error_message:     data.errorMessage ?? null,
    });
    if (error) console.error('[Supabase] logGeneration:', error.message);
  }

  // ── Settings ─────────────────────────────────────────────────────────────
  async loadSetting<T>(key: string): Promise<T | null> {
    const { data, error } = await this.db
      .from('sw_settings').select('value').eq('key', key).single();
    if (error) return null;
    return data?.value as T ?? null;
  }

  async saveSetting(key: string, value: unknown): Promise<void> {
    const { error } = await this.db.from('sw_settings').upsert({ key, value });
    if (error) console.error('[Supabase] saveSetting:', error.message);
  }

  // ── Music Tracks ─────────────────────────────────────────────────────────
  async loadMusicTracks(): Promise<MusicTrack[]> {
    const { data, error } = await this.db
      .from('sw_music_tracks')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) { console.error('[Supabase] loadMusicTracks:', error.message); return []; }
    return (data ?? []) as MusicTrack[];
  }

  async upsertMusicTrack(track: Omit<MusicTrack, 'id' | 'created_at' | 'used_count'>): Promise<MusicTrack | null> {
    const { data, error } = await this.db
      .from('sw_music_tracks')
      .insert({ ...track, used_count: 0 })
      .select()
      .single();
    if (error) { console.error('[Supabase] upsertMusicTrack:', error.message); return null; }
    return data as MusicTrack;
  }

  async incrementMusicTrackUsed(id: string): Promise<void> {
    await this.db.rpc('increment_music_used', { track_id: id });
  }

  async deleteMusicTrack(id: string): Promise<void> {
    const { error } = await this.db.from('sw_music_tracks').delete().eq('id', id);
    if (error) console.error('[Supabase] deleteMusicTrack:', error.message);
  }

  // ── Brand Templates ──────────────────────────────────────────────────────
  async loadBrandTemplates(): Promise<BrandTemplate[]> {
    const { data, error } = await this.db
      .from('sw_brand_templates')
      .select('*')
      .order('is_system', { ascending: false })
      .order('created_at', { ascending: true });
    if (error) { console.error('[Supabase] loadBrandTemplates:', error.message); return []; }
    return (data ?? []) as BrandTemplate[];
  }

  async upsertBrandTemplate(template: Omit<BrandTemplate, 'id' | 'created_at'>): Promise<BrandTemplate | null> {
    // Strip undefined values so Supabase doesn't try to write NULL to missing columns
    const payload = Object.fromEntries(
      Object.entries(template).filter(([, v]) => v !== undefined && v !== null && v !== '')
    );
    const { data, error } = await this.db
      .from('sw_brand_templates')
      .upsert(payload, { onConflict: 'slug' })
      .select()
      .single();
    if (error) { console.error('[Supabase] upsertBrandTemplate:', error.message); return null; }
    return data as BrandTemplate;
  }

  async deleteBrandTemplate(id: string): Promise<void> {
    const { error } = await this.db.from('sw_brand_templates').delete().eq('id', id).eq('is_system', false);
    if (error) console.error('[Supabase] deleteBrandTemplate:', error.message);
  }

  // ── Content Drafts (Prism) ────────────────────────────────────────────────
  async loadContentDrafts(status?: DraftStatus | DraftStatus[]): Promise<ContentDraft[]> {
    let q = this.db
      .from('content_drafts')
      .select('*, content_pipeline_runs(email_from, email_subject, total_duration_ms)')
      .order('created_at', { ascending: false });

    if (status) {
      const statuses = Array.isArray(status) ? status : [status];
      q = q.in('status', statuses);
    }

    const { data, error } = await q;
    if (error) { console.error('[Supabase] loadContentDrafts:', error.message); return []; }
    return (data ?? []) as ContentDraft[];
  }

  async getContentDraft(id: string): Promise<ContentDraft | null> {
    const { data, error } = await this.db
      .from('content_drafts')
      .select('*, content_pipeline_runs(email_from, email_subject, total_duration_ms)')
      .eq('id', id)
      .single();
    if (error) { console.error('[Supabase] getContentDraft:', error.message); return null; }
    return data as ContentDraft;
  }

  async getContentDraftBySlug(slug: string): Promise<ContentDraft | null> {
    const { data, error } = await this.db
      .from('content_drafts')
      .select('*, content_pipeline_runs(email_from, email_subject, total_duration_ms)')
      .eq('slug', slug)
      .single();
    if (error) { console.error('[Supabase] getContentDraftBySlug:', error.message); return null; }
    return data as ContentDraft;
  }

  async updateContentDraft(id: string, patch: Partial<ContentDraft>): Promise<void> {
    const { error } = await this.db.from('content_drafts').update(patch).eq('id', id);
    if (error) console.error('[Supabase] updateContentDraft:', error.message);
  }

  subscribeToContentDrafts(callback: () => void) {
    return this.db
      .channel('content_drafts_changes')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'content_drafts' }, callback)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'content_drafts' }, callback)
      .subscribe();
  }

  // ── Pipeline Runs (Prism) ────────────────────────────────────────────────
  async loadPipelineRuns(limit = 50): Promise<PipelineRun[]> {
    const { data, error } = await this.db
      .from('content_pipeline_runs')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) { console.error('[Supabase] loadPipelineRuns:', error.message); return []; }
    return (data ?? []) as PipelineRun[];
  }

  subscribeToPipelineRuns(callback: () => void, onStatus?: (status: string) => void) {
    return this.db
      .channel('content_pipeline_runs_changes')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'content_pipeline_runs' }, callback)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'content_pipeline_runs' }, callback)
      .subscribe(onStatus);
  }

  // ── Content Sources (Prism) ──────────────────────────────────────────────
  async loadContentSources(): Promise<ContentSource[]> {
    const { data, error } = await this.db
      .from('content_sources')
      .select('*')
      .order('priority', { ascending: false });
    if (error) { console.error('[Supabase] loadContentSources:', error.message); return []; }
    return (data ?? []) as ContentSource[];
  }

  async updateContentSource(id: string, patch: Partial<ContentSource>): Promise<void> {
    const { error } = await this.db.from('content_sources').update(patch).eq('id', id);
    if (error) console.error('[Supabase] updateContentSource:', error.message);
  }

  async insertContentSource(source: Omit<ContentSource, 'id' | 'total_received' | 'total_relevant' | 'total_published'>): Promise<void> {
    const { error } = await this.db.from('content_sources').insert({
      ...source,
      total_received: 0,
      total_relevant: 0,
      total_published: 0,
    });
    if (error) console.error('[Supabase] insertContentSource:', error.message);
  }

  // ── Instagram Token ──────────────────────────────────────────────────────
  async getInstagramToken(): Promise<{ token: string; userId: string } | null> {
    const { data, error } = await this.db
      .from('sw_instagram_tokens')
      .select('access_token, ig_user_id')
      .eq('is_active', true)
      .order('updated_at', { ascending: false })
      .limit(1)
      .single();
    if (error) return null;
    return { token: data.access_token, userId: data.ig_user_id };
  }

  async updateInstagramToken(igUserId: string, token: string): Promise<void> {
    const { error } = await this.db.from('sw_instagram_tokens')
      .update({ access_token: token, updated_at: new Date().toISOString() })
      .eq('ig_user_id', igUserId);
    if (error) console.error('[Supabase] updateInstagramToken:', error.message);
  }

  // ── Week Plans ───────────────────────────────────────────────────────────
  async saveWeekPlan(weekNumber: number, year: number, postsData: unknown[]): Promise<void> {
    const { error } = await this.db.from('sw_week_plans').insert({
      week_number: weekNumber, year, posts_data: postsData, status: 'proposed',
    });
    if (error) console.error('[Supabase] saveWeekPlan:', error.message);
  }

  async acceptWeekPlan(id: string): Promise<void> {
    const { error } = await this.db.from('sw_week_plans')
      .update({ status: 'accepted', accepted_at: new Date().toISOString() })
      .eq('id', id);
    if (error) console.error('[Supabase] acceptWeekPlan:', error.message);
  }

  // ── Stella Memory ─────────────────────────────────────────────────────────

  async insertMemory(data: {
    postId: string;  // text (nicht uuid)
    topicKey: string;
    platform: string;
    contentPillar: string;
    weekTag: string;
    style: string;
    brandName?: string;
    headline: string;
    hook?: string;
    headlineEmbedding?: number[];
    duplicateOf?: string;
  }): Promise<void> {
    const { error } = await this.db.from('stella_memory').insert({
      post_id:             data.postId,
      topic_key:           data.topicKey,
      platform:            data.platform,
      content_pillar:      data.contentPillar,
      week_tag:            data.weekTag,
      style:               data.style,
      brand_name:          data.brandName ?? null,
      headline:            data.headline,
      hook:                data.hook ?? null,
      headline_embedding:  data.headlineEmbedding ? JSON.stringify(data.headlineEmbedding) : null,
      duplicate_of:        data.duplicateOf ?? null,
    });
    if (error) console.error('[Supabase] insertMemory:', error.message);
  }

  async getRecentMemory(topicKey: string, platform: string, limit = 6): Promise<{
    headline: string; hook: string | null; content_pillar: string;
    week_tag: string; style: string; brand_name: string | null;
  }[]> {
    const { data, error } = await this.db.rpc('get_stella_memory', {
      p_topic_key: topicKey,
      p_platform:  platform,
      p_limit:     limit,
    });
    if (error) { console.error('[Supabase] getRecentMemory:', error.message); return []; }
    return data ?? [];
  }

  async checkHeadlineDuplicate(
    topicKey: string, platform: string, embedding: number[], threshold = 0.88
  ): Promise<string | null> {
    const { data, error } = await this.db.rpc('check_headline_duplicate', {
      p_topic_key: topicKey,
      p_platform:  platform,
      p_embedding: JSON.stringify(embedding),
      p_threshold: threshold,
    });
    if (error) { console.error('[Supabase] checkHeadlineDuplicate:', error.message); return null; }
    return data ?? null;
  }

  // ── Image Library ─────────────────────────────────────────────────────────

  async loadImageLibrary(opts: {
    category?: ImageCategory | 'all';
    search?: string;
    limit?: number;
  } = {}): Promise<ImageLibraryImage[]> {
    let q = this.db
      .from('sw_image_library')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(opts.limit ?? 100);

    if (opts.category && opts.category !== 'all') {
      q = q.eq('category', opts.category);
    }
    if (opts.search?.trim()) {
      q = q.or(`name.ilike.%${opts.search}%,description.ilike.%${opts.search}%`);
    }

    const { data, error } = await q;
    if (error) { console.error('[Supabase] loadImageLibrary:', error.message); return []; }
    return (data ?? []) as ImageLibraryImage[];
  }

  async insertImageLibrary(img: Omit<ImageLibraryImage, 'id' | 'created_at'>): Promise<ImageLibraryImage | null> {
    const { data, error } = await this.db
      .from('sw_image_library')
      .insert(img)
      .select()
      .single();
    if (error) { console.error('[Supabase] insertImageLibrary:', error.message); return null; }
    return data as ImageLibraryImage;
  }

  async deleteImageLibrary(id: string): Promise<void> {
    const { error } = await this.db.from('sw_image_library').delete().eq('id', id);
    if (error) console.error('[Supabase] deleteImageLibrary:', error.message);
  }
}
